import { useEffect } from "react";
import { Megaphone, UserRoundPlus } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { useAppDispatch, useAppSelector } from "@/stores/hook";
import { selectCurrentUserId } from "@/features/auth/currentUser";
import { setCurrentLeadId } from "@/features/leads/stores/leadFunctionalSlice";
import {
  assignmentRequestErrorMessage,
  useResolveAssignmentRequest,
} from "@/features/leads/api/useAssignmentRequests";
import { useAlertInboxData, useMarkAlertRead } from "../api/useAlerts";
import { alertTimeAgo, type AssignmentRequestSummary } from "../types";
import {
  closeViewedAlert,
  dismissAnnouncement,
  enqueueAnnouncement,
  useAnnouncementQueue,
  useViewedAlert,
} from "./announceAlert";

/**
 * The ANNOUNCE popup. Mounted once in AppLayout. Owns the inbox query at boot
 * (so the header badge is right from the first paint) and drains its
 * `pendingAnnouncements` into the queue; live announcements arrive through
 * the socket (`applyAlertNewPush`). "Got it" marks the alert read, which is
 * what stops it popping again on the next login. An alert clicked in the Alert
 * box opens here too, and closing that one only closes it.
 *
 * An alert carrying an ASSIGNMENT REQUEST is different: it comes back on
 * every login while the request is pending, whoever has read it. Its buttons
 * are Later (closes it for now), Open lead (go and assign — the request then
 * closes on its own) and Done. The requester sees Cancel instead.
 */
export default function AlertAnnounceHost() {
  const { pendingAnnouncements } = useAlertInboxData();
  const queue = useAnnouncementQueue();
  const viewed = useViewedAlert();
  const markRead = useMarkAlertRead();
  const resolve = useResolveAssignmentRequest();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const currentUserId = useAppSelector(selectCurrentUserId);

  useEffect(() => {
    // Enqueue is idempotent per alert id, so re-fetches are harmless.
    for (const a of pendingAnnouncements) enqueueAnnouncement(a);
  }, [pendingAnnouncements]);

  const current = viewed ?? queue[0];
  if (!current) return null;
  const request = current.request ?? null;

  const acknowledge = () => {
    if (viewed) {
      closeViewedAlert();
      return;
    }
    markRead.mutate(current.id);
    dismissAnnouncement(current.id);
  };

  // A request is not acknowledged by closing it: the unread dot stays as the
  // "still waiting" signal, and the popup returns on the next login.
  const later = () => {
    if (viewed) closeViewedAlert();
    else dismissAnnouncement(current.id);
  };

  const openLead = () => {
    if (!request) return;
    later();
    // Defer past this click so the gesture's tail cannot land on the drawer's overlay.
    window.setTimeout(() => {
      dispatch(setCurrentLeadId(request.leadId));
      if (!location.pathname.startsWith("/leads")) navigate("/leads");
    }, 16);
  };

  const finish = async (action: "done" | "cancel" | "assign") => {
    if (!request) return;
    try {
      await resolve.mutateAsync({ id: request.id, action, alertId: current.id });
      toast.success(
        action === "assign"
          ? `Assigned to ${request.suggested?.name ?? "them"}`
          : action === "done"
            ? "Marked as done"
            : "Request cancelled",
      );
      later();
    } catch (err) {
      toast.error(
        assignmentRequestErrorMessage(
          err,
          action === "assign" ? "Could not assign the lead" : "Could not update the request",
        ),
      );
    }
  };

  const remaining = viewed ? queue.length : queue.length - 1;
  const pending = request?.status === "PENDING";
  const iAsked = request != null && Number(request.requestedBy.id) === Number(currentUserId);
  const suggestedName = request?.suggested?.name?.trim() || null;

  return (
    <Dialog open onOpenChange={(open) => !open && (request ? later() : acknowledge())}>
      <DialogContent className="sm:max-w-[480px] rounded-xl grid-cols-[minmax(0,1fr)]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-5 text-base leading-snug font-semibold text-[#051912]">
            <span
              className={cn(
                "flex size-8 shrink-0 items-center justify-center rounded-full",
                request ? "bg-amber-50" : "bg-[#fff4ec]",
              )}
            >
              {request ? (
                <UserRoundPlus className="size-4 text-amber-600" />
              ) : (
                <Megaphone className="size-4 text-[#ff8447]" />
              )}
            </span>
            <span className="min-w-0 break-words">{current.title}</span>
          </DialogTitle>
          <Separator />
          <DialogDescription className="pt-2 text-[13px] text-[#1e302a]" asChild>
            <div>
              <p className="m-0 max-h-[50vh] overflow-y-auto whitespace-pre-wrap break-words leading-relaxed">
                {current.message}
              </p>
              {request && <RequestStatus request={request} />}
              <p className="m-0 mt-3 text-[11.5px] text-[#697571] break-words">
                {"recipientCount" in current ? (
                  <>
                    Sent to{" "}
                    <span className="font-medium text-[#1e302a]">
                      {current.audienceSummary || "—"}
                    </span>
                    {` · ${current.readCount}/${current.recipientCount} read`}
                  </>
                ) : (
                  <>
                    From{" "}
                    <span className="font-medium text-[#1e302a]">
                      {current.createdBy?.name?.trim() || "Admin"}
                    </span>
                  </>
                )}
                {current.createdAt ? ` · ${alertTimeAgo(current.createdAt)}` : ""}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter className="mt-4 flex items-center justify-between gap-2 sm:justify-between">
          <span className="text-[11px] text-[#697571]">
            {remaining > 0
              ? `${remaining} more alert${remaining === 1 ? "" : "s"} waiting`
              : ""}
          </span>
          {request && pending ? (
            <span className="flex flex-wrap items-center justify-end gap-2">
              <Button variant="ghost" onClick={later} disabled={resolve.isPending}>
                Later
              </Button>
              {iAsked ? (
                <>
                  <Button variant="outline" onClick={openLead} disabled={resolve.isPending}>
                    Open lead
                  </Button>
                  <Button variant="destructive" onClick={() => finish("cancel")} disabled={resolve.isPending}>
                    {resolve.isPending ? "Cancelling…" : "Cancel request"}
                  </Button>
                </>
              ) : suggestedName ? (
                <>
                  {/* "Done" without assigning stays available, but the ask names a person, so assigning IS the answer. */}
                  <Button
                    variant="ghost"
                    onClick={() => finish("done")}
                    disabled={resolve.isPending}
                    title="Close the request without assigning"
                  >
                    Done
                  </Button>
                  <Button variant="outline" onClick={openLead} disabled={resolve.isPending}>
                    Open lead
                  </Button>
                  <Button variant="primary" onClick={() => finish("assign")} disabled={resolve.isPending} autoFocus>
                    {resolve.isPending ? "Assigning…" : `Assign to ${suggestedName}`}
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="ghost" onClick={() => finish("done")} disabled={resolve.isPending}>
                    Done
                  </Button>
                  <Button variant="primary" onClick={openLead} disabled={resolve.isPending} autoFocus>
                    Open lead
                  </Button>
                </>
              )}
            </span>
          ) : (
            <Button variant="primary" onClick={acknowledge} autoFocus>
              Got it
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** "Waiting · asked Branch Manager" / "Done by NiFan · 2h ago". */
function RequestStatus({ request }: { request: AssignmentRequestSummary }) {
  const by = request.resolvedBy?.name?.trim();
  const when = request.resolvedAt ? ` · ${alertTimeAgo(request.resolvedAt)}` : "";
  const text =
    request.status === "PENDING"
      ? `Waiting · asked ${request.target.name}${request.recipientCount > 1 ? ` (${request.recipientCount} people)` : ""}`
      : request.status === "CANCELLED"
        ? `Cancelled${by ? ` by ${by}` : ""}${when}`
        : request.resolution === "assigned"
          ? `Assigned${by ? ` by ${by}` : ""}${when}`
          : `Done${by ? ` by ${by}` : ""}${when}`;
  return (
    <p
      className={cn(
        "m-0 mt-3 inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-medium",
        request.status === "PENDING"
          ? "bg-amber-50 text-amber-700"
          : request.status === "CANCELLED"
            ? "bg-[var(--color-black-1)] text-[var(--color-black-6)]"
            : "bg-[var(--color-green-1)] text-[var(--color-primary)]",
      )}
    >
      {text}
    </p>
  );
}
